import { NgTooltip } from "@/atoms/tooltips/Tooltip";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  CSSProperties,
  Fragment,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { MOBILE_BREAKPOINT } from "@oneleet/config/constants";
import { cn, useScrollFaders } from "@oneleet/utils";

import { AnimatePresence, motion } from "framer-motion";

type RenderHeaderFn = (label: string, index: number) => ReactNode;
type RenderCellFn<T> = (rowItem: T, index: number) => ReactNode;

export interface Column<RowType> {
  label: string;
  sticky?: boolean;
  renderHeader?: RenderHeaderFn;
  renderCell: RenderCellFn<RowType>;
  /**
   * Class name for both the th and td elements
   */
  className?: string;
  thClassName?: string;
  tdClassName?: string;
  innerClassName?: string;
}

export interface Group<RowType> {
  label?: string;
  icon?: ReactNode;
  decorator?: ReactNode;
  filter: (row: RowType) => boolean;
  helperText?: string;
  hideLabel?: boolean;
  preDecorator?: ReactNode;
}

interface Props<RowType> {
  columns: Column<RowType>[];
  groups?: Group<RowType>[];
  rows: RowType[];
  hideColumnBorders?: boolean;
  renderRowActionBar?: (row: RowType) => ReactNode;
  onRowClick?: (row: RowType) => void;
  getRowHref?: (row: RowType) => string;
  className?: string;
  classNames?: {
    root?: string;
    table?: string;
    header?: string;
    body?: string;
    row?: string;
    headerRow?: string;
    tableRow?: string;
    cell?: string;
    cellInner?: string;
    actionBar?: string;
  };
  disableFirstColumnPadding?: boolean;
  rowStyles?: (row: RowType) => CSSProperties;
  disableStickyColumns?: boolean;
  animateRows?: boolean;
}

interface Row {
  id: string;
}

const DataTableContext = createContext<{
  stickyColumnIndex: number;
  tableIsSticking?: boolean;
  isMobile?: boolean;
}>({
  stickyColumnIndex: -1,
  tableIsSticking: false,
  isMobile: false,
});

export default function DataTable<RowType>({
  columns,
  rows,
  groups,
  hideColumnBorders,
  renderRowActionBar,
  onRowClick,
  getRowHref,
  className,
  classNames,
  rowStyles,
  disableStickyColumns,
  disableFirstColumnPadding,
  animateRows,
}: Props<RowType & Row>) {
  const [isMobile, setIsMobile] = useState(false);

  const isUsingGroups = useMemo(() => groups && groups?.length > 0, [groups]);

  const stickyColumnIndex = useMemo(() => {
    if (isMobile || disableStickyColumns) return -1;
    const index = columns.findIndex((column) => column.sticky);
    return index === -1 ? 0 : index;
  }, [columns, disableStickyColumns, isMobile]);

  const { containerRef, scrollIsAtLeft, scrollIsAtRight } = useScrollFaders();

  const stickyHeaderRef = useRef<HTMLTableCellElement>(null);
  const [tableIsSticking, setTableIsSticking] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      setIsMobile(container.offsetWidth < MOBILE_BREAKPOINT);
    });

    const handleScroll = () => {
      if (!stickyHeaderRef.current) return;
      const containerRect = container.getBoundingClientRect();
      const stickyRect = stickyHeaderRef.current.getBoundingClientRect();
      // When the sticky cell's left aligns with the container's left, it's sticking.
      const newTableIsSticking =
        !scrollIsAtLeft && Math.abs(stickyRect.left - containerRect.left) < 1;
      if (newTableIsSticking !== tableIsSticking) {
        setTableIsSticking(newTableIsSticking);
      }
    };

    observer.observe(container);
    container.addEventListener("scroll", handleScroll);

    handleScroll();

    return () => {
      observer.disconnect();
      container.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, scrollIsAtLeft, tableIsSticking]);

  return (
    <DataTableContext.Provider
      value={{ stickyColumnIndex, tableIsSticking, isMobile }}
    >
      <div className={cn("relative w-full", className, classNames?.root)}>
        <div ref={containerRef} className="overflow-x-auto overflow-y-hidden">
          <table className={cn("w-full table-auto", classNames?.table)}>
            <thead
              className={cn(
                "w-full border-y border-neutral-200",
                classNames?.header,
              )}
            >
              <tr
                className={cn("w-full", classNames?.row, classNames?.headerRow)}
              >
                {columns.map((column, index) => {
                  const isFirstColumn = index === 0;
                  const isLastColumn = index === columns.length - 1;
                  const isSticky = index === stickyColumnIndex;
                  const isSticking = tableIsSticking && isSticky;

                  return (
                    <th
                      key={column.label}
                      ref={isSticky ? stickyHeaderRef : undefined}
                      className={cn(
                        "h-[36px]",
                        isFirstColumn
                          ? (disableFirstColumnPadding ?? false)
                            ? "px-5"
                            : "pl-8 pr-5"
                          : "px-5",
                        !isLastColumn &&
                          !hideColumnBorders &&
                          "border-r border-neutral-200",
                        // Note borders and shadows for columns that are sticking only work/show up on tables with grid layouts currently
                        isSticky && "sticky left-0 z-10",
                        isSticking &&
                          "bg-white shadow-[4px_0_6px_-1px_rgba(0,0,0,0.1)]",
                        column.className,
                        column.thClassName,
                      )}
                    >
                      <span className="text-ng-sm font-ng-medium flex h-full items-center whitespace-nowrap uppercase leading-none tracking-[3%] text-neutral-600">
                        {column.renderHeader?.(column.label, index) ??
                          column.label}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className={cn("w-full", classNames?.body)}>
              <AnimatePresence initial={false}>
                {isUsingGroups
                  ? groups?.map((group) => {
                      const filteredRows = rows.filter(group.filter);
                      const numRows = filteredRows.length;

                      const groupPreDecorator = group.preDecorator && (
                        <tr
                          className="w-full"
                          key={`group-pre-decorator-${group.label}`}
                        >
                          <th colSpan={columns.length} className="text-left">
                            {group.preDecorator}
                          </th>
                        </tr>
                      );

                      if (numRows === 0) return groupPreDecorator;

                      return (
                        <Fragment key={group.label}>
                          {groupPreDecorator}
                          <GroupRow
                            group={group}
                            columns={columns}
                            numRows={numRows}
                          />
                          {filteredRows.map((row, index) => (
                            <Row
                              key={row.id}
                              row={row}
                              index={index}
                              columns={columns}
                              hideColumnBorders={hideColumnBorders}
                              classNames={classNames}
                              rowStyles={rowStyles}
                              onRowClick={onRowClick}
                              getRowHref={getRowHref}
                              renderRowActionBar={renderRowActionBar}
                              disableFirstColumnPadding={
                                disableFirstColumnPadding
                              }
                              animateRows={animateRows}
                            />
                          ))}
                        </Fragment>
                      );
                    })
                  : rows.map((row, index) => (
                      <Row
                        key={row.id}
                        row={row}
                        index={index}
                        columns={columns}
                        hideColumnBorders={hideColumnBorders}
                        classNames={classNames}
                        rowStyles={rowStyles}
                        onRowClick={onRowClick}
                        getRowHref={getRowHref}
                        renderRowActionBar={renderRowActionBar}
                        disableFirstColumnPadding={disableFirstColumnPadding}
                        animateRows={animateRows}
                      />
                    ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
        <AnimatePresence>
          {!scrollIsAtRight && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              className="pointer-events-none absolute right-0 top-0 z-10 h-full w-[140px] bg-[linear-gradient(270deg,_rgba(255,255,255,0.90)_0%,_rgba(255,255,255,0.70)_33.33%,_rgba(255,255,255,0.50)_66.67%,_rgba(255,255,255,_0.00)_100%)]"
            />
          )}
        </AnimatePresence>
      </div>
    </DataTableContext.Provider>
  );
}

function GroupRow<T>({
  group,
  columns,
  numRows,
  classNames,
}: Pick<Props<T>, "columns"> & {
  group: Group<T>;
  numRows: number;
  classNames?: { row?: string };
}) {
  return (
    <tr className={cn("w-full", classNames?.row)}>
      <th colSpan={columns.length} className="text-left">
        <div className="box-border flex h-9 flex-row items-center border-b border-neutral-200 bg-neutral-50 py-2">
          <div className="sticky left-0 z-10 inline-flex items-center justify-start gap-2.5 px-8">
            {group.decorator && group.decorator}
            {group.icon && (
              <div className="flex size-4 items-center justify-center">
                {group.icon}
              </div>
            )}
            {group.label && !group.hideLabel && (
              <p className="text-heading-xs font-ng-medium text-neutral-950">
                {group.label}
              </p>
            )}
            <p className="text-ng-md text-neutral-550 font-ng-medium">
              {numRows}
            </p>
            {group.helperText && (
              <NgTooltip delayDuration={50}>
                <NgTooltip.Trigger>
                  <span>
                    <IconInfoCircle className="text-neutral-550 size-4" />
                  </span>
                </NgTooltip.Trigger>
                <NgTooltip.Content side="bottom">
                  <p className="text-ng-md">{group.helperText}</p>
                </NgTooltip.Content>
              </NgTooltip>
            )}
          </div>
        </div>
      </th>
    </tr>
  );
}

function Row<T>({
  row,
  index,
  columns,
  hideColumnBorders,
  classNames,
  rowStyles,
  onRowClick,
  getRowHref,
  renderRowActionBar,
  disableFirstColumnPadding,
  animateRows,
}: Pick<
  Props<T>,
  | "onRowClick"
  | "getRowHref"
  | "columns"
  | "hideColumnBorders"
  | "classNames"
  | "rowStyles"
  | "renderRowActionBar"
  | "disableFirstColumnPadding"
  | "animateRows"
> & {
  row: T;
  index: number;
}) {
  const { stickyColumnIndex, tableIsSticking } = useContext(DataTableContext);
  const isClickable = Boolean(onRowClick || getRowHref);

  const wrapForMotion = (element: ReactNode) =>
    animateRows ? (
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: "auto" }}
        exit={{ height: 0 }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        {element}
      </motion.div>
    ) : (
      element
    );

  return (
    <motion.tr
      className={cn(
        "group border-b border-neutral-200 transition-colors duration-300 hover:duration-0",
        isClickable && "cursor-pointer hover:bg-neutral-50",
        classNames?.row,
        classNames?.tableRow,
      )}
      style={rowStyles?.(row)}
    >
      {columns.map((column, columnIndex) => {
        const isFirstColumn = columnIndex === 0;
        const isLastColumn = columnIndex === columns.length - 1;
        const Element = isFirstColumn ? "th" : "td";
        const InnerElement = typeof getRowHref === "function" ? Link : "div";
        const isSticky = columnIndex === stickyColumnIndex;
        const isSticking = tableIsSticking && isSticky;

        return (
          <Element
            key={columnIndex}
            scope={isFirstColumn ? "row" : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn(
              !isLastColumn &&
                !hideColumnBorders &&
                "border-r border-neutral-200",
              // Note borders and shadows for columns that are sticking only work/show up on tables with grid layouts currently
              isSticky && "sticky left-0 z-10",
              isSticking && "bg-white shadow-[4px_0_6px_-1px_rgba(0,0,0,0.1)]",
              isSticking && isClickable && "group-hover:bg-neutral-50",
              classNames?.cell,
              column.className,
              column.tdClassName,
            )}
          >
            {wrapForMotion(
              <InnerElement
                to={getRowHref?.(row) ?? ""}
                className={cn(
                  "block h-full min-w-28 py-3.5 text-left leading-none",
                  isFirstColumn
                    ? (disableFirstColumnPadding ?? false)
                      ? "px-5"
                      : "pl-8 pr-5"
                    : "px-5",
                  classNames?.cellInner,
                  column.innerClassName,
                )}
              >
                {column.renderCell(row, index)}
              </InnerElement>,
            )}
          </Element>
        );
      })}
      {renderRowActionBar && (
        <td
          className={cn(
            "sticky right-0 z-20 flex items-center opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-hover:duration-0",
            classNames?.actionBar,
          )}
        >
          {/*
           * The absolutely positioned wrapper div with right-0 below ensures that the action bar takes up zero width/space.
           * The h-full and px-8 on the sub-div makes the clickable area of the action bar more forgiving, as otherwise clicking slightly off the action bar triggers a row click,
           * which if the row is clickable, causes the user to navigate away to another page, which is unexpected and frustrating.
           */}
          <div className="absolute right-0 flex h-full items-center px-8">
            {renderRowActionBar(row)}
          </div>
        </td>
      )}
    </motion.tr>
  );
}
